import { useSuspenseQuery } from '@tanstack/react-query';
import { selectVariablesByName } from 'components/editor/grid/reduxStore/allVariableSlice';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { VariableName } from 'components/editor/SidePanel/Variables/interfaces';
import { resolveAssignedUserLegacyId } from 'hooks/resolveAssignedUserLegacyId';
import { useAccountUsersQuery } from 'hooks/useAccountUsersQuery';
import { useApprovalAccess } from 'hooks/useApprovalAccess';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { usePermissionsContextValue } from 'providers/PermissionsProvider';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { usersPermissionsQueries } from 'services/access-control/users/usersPermissionsQueries';
import { UserPermission } from 'services/permissions/entities/UserPermissions';
import { FeatureFlags } from 'utils/featureFlags';
import { ContentType } from 'utils/getContentType';
import { URLS } from 'utils/urls';
import { ShareDocumentV3 } from './ShareDocumentV3';

export function DocumentShare() {
  const contentId = useAppSelector(selectContentId);
  const variablesByName = useSelector(selectVariablesByName);
  const { t } = useTranslation();

  const { userPermissions } = usePermissionsContextValue();
  const isCollaborator = userPermissions.includes(UserPermission.isCollaborator);

  const { [FeatureFlags.permissionsEnforcement]: isPermissionsEnforcementEnabled } = useFeatureFlag([FeatureFlags.permissionsEnforcement]);
  const {
    data: { permissions, source },
  } = useSuspenseQuery(usersPermissionsQueries.permissions);
  const canShareDocument =
    !isCollaborator && (!isPermissionsEnforcementEnabled || source === 'fallback' || permissions.share_document.allowed);

  const contentName = String(variablesByName[VariableName.ProposalName]?.value);
  const documentNameFallback = t('editor.default_document_title');
  const documentTitleWithCompanyName = contentName ?? documentNameFallback;

  const { data: documentData } = useContentMetadata(ContentType.DOCUMENT, contentId);
  const { data: accountUsers } = useAccountUsersQuery();
  const assignedUserLegacyId = resolveAssignedUserLegacyId(documentData?.assignedUser);

  const approvalAccess = useApprovalAccess(contentId);

  const previewUrlWithSenderInfo = useMemo(() => {
    const previewURL = URLS.previewByContentId(contentId);
    const sender = accountUsers?.find((user) => String(user.id) === assignedUserLegacyId);
    if (!sender) return previewURL;

    const { email: senderEmail, fullName: senderFullName } = sender;
    const senderInfo = senderFullName && senderEmail ? `?sender_name=${senderFullName}&sender_email=${senderEmail}` : '';
    return `${previewURL}${senderInfo}`;
  }, [contentId, accountUsers, assignedUserLegacyId]);

  if (!contentId) return null;

  return (
    <ShareDocumentV3
      previewURL={previewUrlWithSenderInfo}
      documentTitleWithCompanyName={String(documentTitleWithCompanyName)}
      canShare={canShareDocument}
      approvalAccess={approvalAccess}
    />
  );
}
